import { HtmlRenderer } from '@eliadmin/core/src/components/ui/html-renderer';
import { stripTags } from '@eliadmin/core/src/utils/dlz/text';

import type { TextField } from '@eliadmin/core/src/components/dlz/product-detail/details/product-detail-content';

const LIST = /<(ol|ul)\b/gi;
const ITEM = /<li\b[^>]*>([\s\S]*?)<\/li\s*>/gi;

/**
 * Items of a field that is exactly one ordered list (the steps of a manual), as plain text;
 * any other HTML gives none.
 */
function stepsOf(html: string): string[] {
  const value = html.trim();
  if (!/^<ol\b[\s\S]*<\/ol\s*>$/i.test(value) || (value.match(LIST) ?? []).length !== 1) return [];
  return [...value.matchAll(ITEM)].map(([, item]) => stripTags(item)).filter(Boolean);
}

/**
 * Text fields of the product under the description, one card each (Figma 60137:7643): an ordered
 * list becomes the numbered steps (red number on a bg/surface circle – 24 px and centred on the
 * text on phones, Figma 60459:102817), other text a titled block. The field name is the title.
 */
export function ProductTextFields({
  fields,
  titleLevel,
}: {
  fields: TextField[];
  titleLevel: 'h2' | 'h3';
}) {
  const Title = titleLevel;

  return (
    <>
      {fields.map(field => {
        const steps = stepsOf(field.value);
        return (
          <div
            key={field.id ?? field.name}
            className="flex flex-col gap-3 rounded-[18px] border border-[#E2E4E7] p-4 md:p-6"
          >
            <Title className="dlz-h5 text-[#0F1012]">{field.name}</Title>
            {steps.length ? (
              <ol role="list" className="flex flex-col gap-2 md:gap-3">
                {steps.map((step, index) => (
                  <li key={index} className="flex items-center gap-3 md:items-start">
                    <span
                      aria-hidden="true"
                      className="dlz-body-s-medium flex size-6 shrink-0 items-center justify-center rounded-full bg-[#F5F6F7] text-[#D7191F] md:size-8"
                    >
                      {index + 1}
                    </span>
                    <span className="dlz-body-m min-w-0 text-[#0F1012] md:pt-[5px]">{step}</span>
                  </li>
                ))}
              </ol>
            ) : (
              <HtmlRenderer
                html={field.value}
                className="dlz-rich dlz-body-m text-[#4F545C] [&>:first-child]:mt-0 [&>:last-child]:mb-0"
              />
            )}
          </div>
        );
      })}
    </>
  );
}
